/**
 * NotificationLogPage — coach-only dispatch history (Apr 2026).
 *
 * Shows every email + in-app notification the system has sent FOR
 * THIS TEAM: who got it, what kind, when, status. Populated by
 * GET /teams/:teamId/notification-log, which merges email_logs and
 * notifications into a single time-ordered stream.
 *
 * Privacy model:
 *   • Recipient NAME only (never raw email) — matches backend
 *   • Title/subject shown only for in-app notifications (emails'
 *     subject lines can contain PII like "removed from roster")
 *   • Body never shown — fetch via admin tools if needed
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Bell, Mail, Smartphone, ChevronLeft, ChevronDown, ChevronRight, Filter, CheckCircle, XCircle, Clock, AlertCircle, Download, FileSpreadsheet, Search,
} from 'lucide-react';
import api from '../services/api.js';
import toast from 'react-hot-toast';
import { useTeamRole } from '../hooks/useTeamRole.js';
import { xlsxBlob } from '../utils/tableExport.js';

const CHANNEL_META = {
  email:  { icon: Mail,       label: 'Email',  color: 'var(--ice)'  },
  in_app: { icon: Bell,       label: 'In-app', color: 'var(--zen)'  },
  push:   { icon: Smartphone, label: 'Push',   color: 'var(--puck)' },
};

const STATUS_META = {
  sent:    { icon: CheckCircle, color: 'var(--goal)',    label: 'Sent'    },
  failed:  { icon: XCircle,     color: 'var(--penalty)', label: 'Failed'  },
  skipped: { icon: AlertCircle, color: 'var(--text-2)',  label: 'Skipped' },
};

// Friendly labels for the trigger column. Falls back to the raw
// trigger string when unknown — better than showing nothing so the
// coach can report surprising values.
const TRIGGER_LABELS = {
  auto_nudge:          'Auto-jab',
  spare_auto_invite:   'Spare auto-invite',
  game_day_reminder:   'Game-day reminder',
  auto_balance:        'Team assignment (auto-balance)',
  manual_reminder:     'Manual reminder',
  invite:              'Invite email',
  chat_mention:        'Chat mention',
  drinks_reminder:     'Drinks reminder',
  // v2.5.219 — coach checkout alerts now carry a tier-specific trigger
  // so the log shows whether a PLAYER or a GOALIE (full-time vs spare)
  // checked out, instead of one generic "coach_checkout_notify" row.
  // `coach_checkout_notify` is the legacy value kept for entries logged
  // before this split (90-day retention means they age out on their own).
  coach_checkout_notify:        'Coach alert — checkout',
  coach_checkout_ft_player:     'Coach alert — FT player checked out',
  coach_checkout_ft_goalie:     'Coach alert — FT goalie checked out',
  coach_checkout_spare_player:  'Coach alert — spare player checked out',
  coach_checkout_spare_goalie:  'Coach alert — spare goalie checked out',
  // v2.5.226 — manual "Invite Spares" sends now tag the audience (which
  // Players/Goalies/All tab the coach sent from) so the log shows it.
  manual_spare_request:               'Spare request — all',
  manual_spare_request_players:       'Spare request — players',
  manual_spare_request_goalies:       'Spare request — goalies',
  manual_spare_request_free:          'Spare request (free) — all',
  manual_spare_request_free_players:  'Spare request (free) — players',
  manual_spare_request_free_goalies:  'Spare request (free) — goalies',
};

const formatTimestamp = (iso) => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    // Format: "Wed Apr 22, 9:04 AM" — compact but unambiguous
    const day   = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const time  = d.toLocaleTimeString('en-US',  { hour: 'numeric', minute: '2-digit' });
    return `${day}, ${time}`;
  } catch { return '—'; }
};

export default function NotificationLogPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { isCoach, loading: roleLoading } = useTeamRole(teamId);

  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [channelFilter, setChannelFilter] = useState(''); // '' = all
  const [statusFilter,  setStatusFilter]  = useState(''); // '' = all

  // May 4 2026 — coach feedback: "group the reminders like the
  // super-admin notifications area. One row per dispatch with X sent
  // / X failed counts, expandable to see recipients. Plus a name
  // search box and a trigger-type dropdown." Default to Grouped
  // since the flat view buried single weekly-reminder dispatches
  // behind 42 separate rows. Flat is kept as a drill-down option.
  const [viewMode, setViewMode]      = useState('grouped'); // 'grouped' | 'flat'
  const [dispatches, setDispatches]  = useState([]);
  const [triggerFilter, setTriggerFilter] = useState('');   // '' = any
  const [search, setSearch]          = useState('');         // controlled input
  const [searchActive, setSearchActive] = useState('');      // committed value (Enter / Apply)
  const [expanded, setExpanded]      = useState(() => new Set()); // dispatch keys
  const [triggerOptions, setTriggerOptions] = useState([]);

  // Load the trigger list once per team (independent of filters since
  // it powers the dropdown — should reflect 90 days of history regardless
  // of what the coach has currently filtered to). Cheap, single query.
  useEffect(() => {
    let cancelled = false;
    api.get(`/teams/${teamId}/notification-log/triggers`)
      .then(({ data }) => { if (!cancelled) setTriggerOptions(data.triggers || []); })
      .catch(() => { /* non-fatal — dropdown stays empty if it fails */ });
    return () => { cancelled = true; };
  }, [teamId]);

  // Build query params once per filter change. Used by BOTH flat
  // and grouped fetches (they share filter semantics; the grouped
  // endpoint takes a few extras: trigger + q).
  const buildParams = useCallback((before) => {
    const p = { limit: viewMode === 'grouped' ? 30 : 50 };
    if (channelFilter)  p.channel  = channelFilter;
    if (statusFilter)   p.status   = statusFilter;
    if (triggerFilter)  p.trigger  = triggerFilter;
    if (searchActive)   p.q        = searchActive;
    if (before)         p.before   = before;
    return p;
  }, [channelFilter, statusFilter, triggerFilter, searchActive, viewMode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (viewMode === 'grouped') {
        const { data } = await api.get(
          `/teams/${teamId}/notification-log/dispatches`,
          { params: buildParams(null) },
        );
        setDispatches(data.dispatches || []);
        setNextCursor(null); // grouped view uses page-based pagination if added later
      } else {
        const { data } = await api.get(`/teams/${teamId}/notification-log`, {
          params: buildParams(null),
        });
        setEvents(data.events || []);
        setNextCursor(data.nextCursor || null);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load notification log');
      if (viewMode === 'grouped') setDispatches([]); else setEvents([]);
    }
    setLoading(false);
  }, [teamId, buildParams, viewMode]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/teams/${teamId}/notification-log`, {
        params: buildParams(nextCursor),
      });
      setEvents(prev => [...prev, ...(data.events || [])]);
      setNextCursor(data.nextCursor || null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load more');
    }
    setLoadingMore(false);
  };

  useEffect(() => { load(); }, [load]);

  // Apr 2026 — CSV/Excel export. Hits the same endpoint with
  // `?export=1` so the server lifts the row cap from 200 to 2000,
  // giving the coach a one-shot download of the full filtered
  // history. Filters in effect (channel/status) are applied to the
  // export so what they see is what they download.
  //
  // Both formats include the same columns; CSV is for tools that
  // can't open .xlsx (mail filters, some scripts), XLSX is for
  // people who want to open it in Excel/Numbers/Sheets directly.
  const [exporting, setExporting] = useState(false);

  const fetchAllForExport = async () => {
    const params = { limit: 2000, export: 1 };
    if (channelFilter) params.channel = channelFilter;
    if (statusFilter)  params.status  = statusFilter;
    const { data } = await api.get(`/teams/${teamId}/notification-log`, { params });
    return data.events || [];
  };

  // Build the rows that go into both CSV and XLSX. Single source of
  // truth so the two formats can never drift. Field order is
  // deliberate: timestamp first for sorting, then who/what/result.
  const buildExportRows = (rawEvents) => rawEvents.map(ev => ({
    'Sent At':       ev.createdAt ? new Date(ev.createdAt).toISOString() : '',
    'Channel':       CHANNEL_META[ev.channel]?.label || ev.channel || '',
    'Status':        STATUS_META[ev.status]?.label   || ev.status  || '',
    'Trigger':       TRIGGER_LABELS[ev.trigger]      || ev.trigger || '',
    'Template':      ev.template || '',
    'Recipient':     ev.recipientName || '',
    'Sender':        ev.senderName || '',
    'Provider':      ev.provider || '',
    'Game ID':       ev.gameId || '',
    'Event ID':      ev.id || '',
  }));

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCSV = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllForExport();
      if (all.length === 0) {
        toast('No events to export', { icon: '📭' });
        setExporting(false);
        return;
      }
      const exportRows = buildExportRows(all);
      // CSV-escape: wrap fields in quotes and escape internal quotes
      // by doubling. Newlines inside fields stay inside the quotes —
      // Excel/Numbers/LibreOffice handle that correctly.
      const headers = Object.keys(exportRows[0]);
      const escapeCell = (v) => {
        const s = (v ?? '').toString();
        // Always quote — simpler than detecting commas/quotes/newlines case-by-case.
        return `"${s.replace(/"/g, '""')}"`;
      };
      const csv = [
        headers.map(escapeCell).join(','),
        ...exportRows.map(row => headers.map(h => escapeCell(row[h])).join(',')),
      ].join('\n');

      const stamp = new Date().toISOString().slice(0,10);
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      // Leading BOM so Excel auto-detects UTF-8 (otherwise emoji/accents
      // render as mojibake when opened on Windows Excel).
      triggerDownload(blob, `notification-log-${stamp}.csv`);
      toast.success(`Exported ${all.length} events to CSV`);
      // 2000 is the server-side cap. If we hit it exactly, there are
      // probably more events on older pages — flag that so the coach
      // knows to narrow filters or use the in-app "Load more" pager
      // first if they need everything.
      if (all.length === 2000) {
        toast('Only the most recent 2000 events were exported. Narrow filters for older history.', { icon: 'ℹ️', duration: 6000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to export CSV');
    }
    setExporting(false);
  };

  const exportXLSX = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllForExport();
      if (all.length === 0) {
        toast('No events to export', { icon: '📭' });
        setExporting(false);
        return;
      }
      const exportRows = buildExportRows(all);
      // xlsxBlob lazy-loads the XLSX writer, so it stays out of the main
      // bundle for users who never click export (2026-10-01: write-excel-file
      // via the shared helper in utils/tableExport.js; was SheetJS 0.18.5).
      const headers = Object.keys(exportRows[0]);
      const stamp = new Date().toISOString().slice(0,10);
      const blob = await xlsxBlob({
        sheet: 'Notifications',
        rows: [headers, ...exportRows.map(r => headers.map(h => r[h]))],
        // Column widths (characters) so the sheet is readable on first open.
        // Sent At, Channel, Status, Trigger, Template, Recipient, Sender,
        // Provider, Game ID (UUID), Event ID.
        widths: [22, 10, 10, 22, 18, 22, 22, 12, 38, 38],
      });
      triggerDownload(blob, `notification-log-${stamp}.xlsx`);
      toast.success(`Exported ${all.length} events to Excel`);
      if (all.length === 2000) {
        toast('Only the most recent 2000 events were exported. Narrow filters for older history.', { icon: 'ℹ️', duration: 6000 });
      }
    } catch (err) {
      console.error('XLSX export failed:', err);
      toast.error('Failed to export Excel file');
    }
    setExporting(false);
  };

  // Route guard — non-coaches get a friendly bounce back to the
  // dashboard. useTeamRole returns isCoach=true for both coaches and
  // managers since they share most permissions.
  if (!roleLoading && !isCoach) {
    return (
      <div className="animate-fade-in">
        <div className="card p-6 text-center">
          <h2 className="text-lg font-bold mb-2">Coach-only</h2>
          <p className="text-secondary text-sm mb-4">
            This page shows the team's notification dispatch history.
            Only coaches and managers can view it.
          </p>
          <button onClick={() => navigate('/dashboard')} className="btn-ice mx-auto">
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-4">
        <button onClick={() => navigate(-1)}
          className="text-xs text-secondary hover:text-ice transition-colors flex items-center gap-1 mb-2">
          <ChevronLeft size={14} /> Back
        </button>
        <h1 className="section-title text-3xl">Notification Log</h1>
        <p className="text-secondary text-sm mt-1">
          Every email and in-app notification the system sent for this team.
          Retained for 90 days.
        </p>

        {/* Apr 2026 — Export. Filters in effect (channel/status) apply
            to the export, so coaches can grab e.g. "all failed emails
            in the last 90 days" by filtering to status=failed first
            and then clicking CSV. Up to 2000 rows per download. */}
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            onClick={exportCSV}
            disabled={exporting || loading}
            className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text-2)',
              opacity: (exporting || loading) ? 0.5 : 1,
              cursor: (exporting || loading) ? 'not-allowed' : 'pointer',
            }}>
            <Download size={13} /> Export CSV
          </button>
          <button
            type="button"
            onClick={exportXLSX}
            disabled={exporting || loading}
            className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text-2)',
              opacity: (exporting || loading) ? 0.5 : 1,
              cursor: (exporting || loading) ? 'not-allowed' : 'pointer',
            }}>
            <FileSpreadsheet size={13} /> Export Excel
          </button>
        </div>
      </div>

      {/* Filter card */}
      <div className="card mb-4" style={{ padding: '12px 14px' }}>
        <div className="flex items-center gap-2 mb-3">
          <Filter size={12} style={{ color: 'var(--text-2)' }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-2)' }}>
            Filters
          </span>
        </div>

        {/* May 4 2026 — view-mode segmented control. Grouped collapses
            mass sends into one row per dispatch with counts; Flat is
            the legacy per-recipient view. Default Grouped. */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-2)' }}>
            View:
          </span>
          <div style={{ display: 'inline-flex', padding: 3, background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
            {[
              { id: 'grouped', label: 'Grouped' },
              { id: 'flat',    label: 'Flat' },
            ].map(opt => (
              <button key={opt.id} onClick={() => setViewMode(opt.id)}
                style={{
                  padding: '5px 12px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                  background: viewMode === opt.id ? 'var(--ice)' : 'transparent',
                  color: viewMode === opt.id ? 'var(--btn-accent-text)' : 'var(--text-2)',
                  border: 'none', cursor: 'pointer',
                }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search + trigger dropdown row */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 200 }}>
            <Search size={13} style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-3)', pointerEvents: 'none',
            }} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setSearchActive(search.trim());
                if (e.key === 'Escape') { setSearch(''); setSearchActive(''); }
              }}
              onBlur={() => setSearchActive(search.trim())}
              placeholder="Search by name, template, or trigger…"
              style={{
                width: '100%', padding: '7px 10px 7px 30px',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text)', fontSize: 12,
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          <select
            value={triggerFilter}
            onChange={(e) => setTriggerFilter(e.target.value)}
            aria-label="Filter by reminder type"
            style={{
              padding: '7px 10px',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text)', fontSize: 12,
              cursor: 'pointer', outline: 'none',
              minWidth: 180, maxWidth: 260,
            }}>
            <option value="">All reminder types</option>
            {triggerOptions.map(t => {
              const label = TRIGGER_LABELS[t.value] || t.value.replace(/_/g, ' ');
              return (
                <option key={t.value} value={t.value}>
                  {label} ({t.count})
                </option>
              );
            })}
          </select>
        </div>

        <div className="flex flex-wrap gap-2 mb-2">
          {[{ v: '', l: 'All channels' }, { v: 'email', l: '📧 Email' }, { v: 'in_app', l: '🔔 In-app' }, { v: 'push', l: '📱 Push' }].map(({ v, l }) => (
            <button key={v || 'all'}
              onClick={() => setChannelFilter(v)}
              className="text-xs font-semibold px-3 py-1.5 rounded-full transition-all"
              style={{
                background: channelFilter === v ? 'var(--ice-a15)' : 'var(--surface-2)',
                color: channelFilter === v ? 'var(--ice)' : 'var(--text-2)',
                border: `1px solid ${channelFilter === v ? 'var(--ice-a30)' : 'var(--border)'}`,
              }}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {[{ v: '', l: 'Any status' }, { v: 'sent', l: '✅ Sent' }, { v: 'failed', l: '❌ Failed' }, { v: 'skipped', l: '⏭ Skipped' }].map(({ v, l }) => (
            <button key={v || 'any'}
              onClick={() => setStatusFilter(v)}
              className="text-xs font-semibold px-3 py-1.5 rounded-full transition-all"
              style={{
                background: statusFilter === v ? 'var(--ice-a15)' : 'var(--surface-2)',
                color: statusFilter === v ? 'var(--ice)' : 'var(--text-2)',
                border: `1px solid ${statusFilter === v ? 'var(--ice-a30)' : 'var(--border)'}`,
              }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4,5].map(i => <div key={i} className="skeleton h-16 rounded-lg" />)}
        </div>
      ) : viewMode === 'grouped' ? (
        // ── Grouped view ────────────────────────────────────────────
        // Each card = one dispatch (a batch of recipients sent within
        // the same minute). Click to expand and see the full recipient
        // list with per-row status. Empty state mirrors the flat view.
        dispatches.length === 0 ? (
          <div className="card p-6 text-center">
            <Bell size={28} style={{ color: 'var(--text-3)', margin: '0 auto 8px' }} />
            <p className="text-secondary text-sm">
              No dispatches in the log yet{(channelFilter || statusFilter || triggerFilter || searchActive) ? ' matching your filters' : ''}.
              The log populates as the system sends reminders, invites, and alerts.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {dispatches.map((d, i) => {
                // Stable key per dispatch — no server id since it's a
                // synthetic group, but the bucket-key components are
                // unique enough.
                const key = `${d.kind}|${d.template || ''}|${d.trigger || ''}|${d.createdAt}|${i}`;
                const isOpen = expanded.has(key);
                const channelMeta = CHANNEL_META[d.kind] || CHANNEL_META.in_app;
                const ChIcon = channelMeta.icon;
                const triggerLabel = TRIGGER_LABELS[d.trigger] || d.trigger || d.template || '—';
                const total = d.counts.sent + d.counts.failed + d.counts.skipped;
                return (
                  <div key={key} style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    overflow: 'hidden',
                  }}>
                    {/* Dispatch header — clickable to expand */}
                    <button
                      type="button"
                      onClick={() => {
                        setExpanded(prev => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key); else next.add(key);
                          return next;
                        });
                      }}
                      style={{
                        width: '100%',
                        display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 12,
                        padding: '12px 14px',
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        textAlign: 'left', alignItems: 'center',
                      }}
                      aria-expanded={isOpen}
                      aria-label={`${triggerLabel} dispatch — ${total} recipients, click to ${isOpen ? 'collapse' : 'expand'}`}
                    >
                      {/* Channel icon */}
                      <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: `${channelMeta.color}15`,
                        border: `1px solid ${channelMeta.color}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <ChIcon size={14} style={{ color: channelMeta.color }} />
                      </div>
                      {/* Trigger label + timestamp + sender */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{
                          fontSize: 14, fontWeight: 700, color: 'var(--text)',
                          marginBottom: 2, lineHeight: 1.3,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {triggerLabel}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.3 }}>
                          {formatTimestamp(d.createdAt)}
                          {d.senderName && <span> · by {d.senderName}</span>}
                        </div>
                      </div>
                      {/* Counts pills — sent / failed / skipped */}
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {d.counts.sent > 0 && (
                          <span style={{
                            fontSize: 11, fontWeight: 700,
                            padding: '3px 8px', borderRadius: 999,
                            background: 'rgba(0,232,122,0.12)', color: 'var(--goal)',
                            border: '1px solid rgba(0,232,122,0.30)',
                          }}>✓ {d.counts.sent}</span>
                        )}
                        {d.counts.failed > 0 && (
                          <span style={{
                            fontSize: 11, fontWeight: 700,
                            padding: '3px 8px', borderRadius: 999,
                            background: 'rgba(255,68,68,0.12)', color: 'var(--penalty)',
                            border: '1px solid rgba(255,68,68,0.30)',
                          }}>✗ {d.counts.failed}</span>
                        )}
                        {d.counts.skipped > 0 && (
                          <span style={{
                            fontSize: 11, fontWeight: 700,
                            padding: '3px 8px', borderRadius: 999,
                            background: 'var(--surface-3)', color: 'var(--text-2)',
                            border: '1px solid var(--border)',
                          }}>⏭ {d.counts.skipped}</span>
                        )}
                      </div>
                      {/* Chevron */}
                      <div style={{ flexShrink: 0, color: 'var(--text-3)' }}>
                        {isOpen
                          ? <ChevronDown size={16} />
                          : <ChevronRight size={16} />}
                      </div>
                    </button>

                    {/* Expandable recipient list */}
                    {isOpen && (
                      <div style={{
                        borderTop: '1px solid var(--border)',
                        padding: '8px 14px',
                        background: 'var(--surface-1)',
                      }}>
                        {d.recipients.length === 0 ? (
                          <div className="text-secondary text-xs" style={{ padding: 8 }}>
                            No recipient detail.
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {d.recipients.map(r => {
                              const statusMeta = STATUS_META[r.status] || STATUS_META.sent;
                              const StIcon = statusMeta.icon;
                              return (
                                <div key={r.id} style={{
                                  display: 'grid', gridTemplateColumns: '1fr auto', gap: 8,
                                  padding: '6px 4px',
                                  fontSize: 12,
                                  alignItems: 'center',
                                }}>
                                  <div style={{ minWidth: 0 }}>
                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                                      {r.recipientName || <em style={{ color: 'var(--text-3)' }}>Deleted user</em>}
                                    </span>
                                    {r.error && (
                                      <span style={{ color: 'var(--penalty)', fontSize: 11, marginLeft: 6 }}>
                                        — {r.error}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    color: statusMeta.color, fontSize: 10, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: '0.04em',
                                  }}>
                                    <StIcon size={11} />
                                    {statusMeta.label}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-center mt-4" style={{ color: 'var(--text-3)' }}>
              Showing {dispatches.length} dispatch{dispatches.length === 1 ? '' : 'es'} · Retained 90 days
            </p>
          </>
        )
      ) : events.length === 0 ? (
        <div className="card p-6 text-center">
          <Bell size={28} style={{ color: 'var(--text-3)', margin: '0 auto 8px' }} />
          <p className="text-secondary text-sm">
            No notifications in the log yet{(channelFilter || statusFilter) ? ' matching your filters' : ''}.
            The log populates as the system sends reminders, invites, and alerts.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {events.map(ev => {
              const channelMeta = CHANNEL_META[ev.channel] || CHANNEL_META.in_app;
              const statusMeta  = STATUS_META[ev.status] || STATUS_META.sent;
              const ChIcon = channelMeta.icon;
              const StIcon = statusMeta.icon;
              const triggerLabel = TRIGGER_LABELS[ev.trigger] || ev.trigger || ev.template || '—';
              return (
                <div key={ev.id}
                  style={{
                    display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12,
                    padding: '12px 14px',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    alignItems: 'flex-start',
                  }}>
                  {/* Channel icon */}
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: `${channelMeta.color}15`,
                    border: `1px solid ${channelMeta.color}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <ChIcon size={14} style={{ color: channelMeta.color }} />
                  </div>
                  {/* Middle: recipient + trigger + time */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 700, color: 'var(--text)',
                      marginBottom: 2, lineHeight: 1.3,
                    }}>
                      {ev.recipientName || <em style={{ color: 'var(--text-3)' }}>Deleted user</em>}
                    </div>
                    <div style={{
                      fontSize: 12, color: 'var(--text-2)', lineHeight: 1.35, marginBottom: 2,
                    }}>
                      {triggerLabel}
                      {ev.senderName && (
                        <span style={{ color: 'var(--text-3)' }}> · by {ev.senderName}</span>
                      )}
                    </div>
                    {/* In-app notifications have a title worth showing
                        (coach-facing, short). Emails intentionally do
                        not expose their subject line to the coach
                        because templates can include PII like "removed
                        from roster". */}
                    {ev.channel !== 'email' && ev.title && (
                      <div style={{
                        fontSize: 11, fontStyle: 'italic',
                        color: 'var(--text-3)', lineHeight: 1.35, marginBottom: 2,
                      }}>
                        “{ev.title}”
                      </div>
                    )}
                    <div style={{
                      fontSize: 11, color: 'var(--text-3)',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      <Clock size={10} />
                      {formatTimestamp(ev.createdAt)}
                    </div>
                  </div>
                  {/* Status badge */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '3px 8px', borderRadius: 6,
                    background: `${statusMeta.color}15`,
                    border: `1px solid ${statusMeta.color}30`,
                    flexShrink: 0,
                  }}>
                    <StIcon size={11} style={{ color: statusMeta.color }} />
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      color: statusMeta.color,
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      {statusMeta.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {nextCursor && (
            <div className="text-center mt-4">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="btn-ghost text-xs"
                style={{ padding: '8px 20px' }}>
                {loadingMore ? 'Loading…' : 'Load older entries'}
              </button>
            </div>
          )}
          <p className="text-xs text-center mt-4" style={{ color: 'var(--text-3)' }}>
            Showing {events.length} entries · Retained 90 days
          </p>
        </>
      )}
    </div>
  );
}
